// The Match module's card (?live=1; teams and matches, Ben 2026-10-30 — docs/modules/teams-and-matches.md
// "Match"). Drop a match on it (a result's score in Match Results, a game's bar in a team's
// Matches) and it shows the game: the league and date, both teams and the score, the goals and
// red cards with their minutes, then possession and a bar per stat, each team's number at its end
// (Ben, 2026-10-31). A
// team's name drags out as the team, a scorer's or sent-off player's as the player (Ben,
// 2026-10-31). The header is what the match carries; the rest is /team-match-stats (the home
// side's), loaded on its own so the header shows at once. Not yet: the venue.

import { Suspense, type PointerEvent as ReactPointerEvent } from "react";
import type { MatchStatsTeam } from "@catbento/shared";
import type { TeamMatchMoment } from "@catbento/shared";
import { useLiveLeagueChoices } from "../hooks/useLeagueTable.js";
import { useLiveQuery } from "../live/query.js";
import { liveApi } from "../live/api.js";
import { LivePartBoundary } from "../live/LiveBoundary.js";
import { gameDate } from "../live/team.js";
import type { MatchRef } from "../live/match.js";
import { loadTeamMatchDetail, momentInitialName, type TeamMatchDetail } from "../live/team-matches.js";
import { homeAway, loadMatchStats, matchStatRows } from "../live/match-stats.js";
import { startPlayerTokenDragOnMove, startTeamTokenDragOnMove } from "../canvas/menu-drag.js";
import { setStickyPlayer } from "../canvas/sticky-player.js";
import { noteRowPlayer } from "../live/player-gender.js";
import { CountryFlag } from "./CountryFlag.js";
import { clockMinute } from "../data/match-minute.js";

function TeamName({ match, side }: { match: MatchRef; side: "home" | "away" }) {
  const t = match[side];
  const onPress = (e: ReactPointerEvent<HTMLSpanElement>) => {
    if (window.innerWidth <= 768 || e.button !== 0) return;
    e.stopPropagation();
    startTeamTokenDragOnMove(e.nativeEvent, { id: t.id, name: t.name, competitionId: match.competitionId, season: match.season });
  };
  return (
    <span className={`match-card-team match-card-team-${side} team-draggable`} onPointerDown={onPress}>
      {t.name}
    </span>
  );
}

export function MatchCard({ match, mod }: { match: MatchRef; mod: HTMLElement }) {
  const league = useLiveLeagueChoices().find((c) => c.id === match.competitionId) ?? null;
  const womens = league?.womens ?? null;
  // The teams and score show at once; the goals and reds, then the stats, join them when loaded.
  // Each part fails on its own: the rest stays (the error is logged).
  const plain = <Scoreboard match={match} detail={null} womens={womens} />;
  return (
    <>
      {/* Plain: the game goes onto a map by dragging the whole module (Ben, 2026-10-31: the line
          was "a link to itself"). */}
      <div className="bio-legal bio-team-league">
        <CountryFlag code={league?.countryCode ?? null} />
        {league?.label ?? "—"} · {gameDate(match)}
      </div>
      <Suspense fallback={plain}>
        <LivePartBoundary mod={mod} fallback={() => plain}>
          <ScoreboardWithDetail match={match} womens={womens} />
        </LivePartBoundary>
      </Suspense>
      <Suspense fallback={<div className="match-card-loading">Loading the stats…</div>}>
        <LivePartBoundary mod={mod} fallback={() => null}>
          <MatchStats match={match} />
        </LivePartBoundary>
      </Suspense>
    </>
  );
}

function ScoreboardWithDetail({ match, womens }: { match: MatchRef; womens: boolean | null }) {
  // The home side's view of its season: the same answer a team's Matches uses, so they share it.
  const detail = useLiveQuery(`matches:team:${match.home.id}:${match.competitionId}:${match.season}`, () =>
    loadTeamMatchDetail(liveApi(), match.home.id, match.competitionId, match.season),
  )!;
  return <Scoreboard match={match} detail={detail} womens={womens} />;
}

/** Home on the left, away on the right, each with its colour (the site's first two comparison
 *  colours: club colours are empty in our database). Under them, each side's goals and red cards
 *  on its own side. Both teams in the same white (Ben, 2026-10-31). */
function Scoreboard({ match, detail, womens }: { match: MatchRef; detail: TeamMatchDetail | null; womens: boolean | null }) {
  const moments = detail?.moments.get(match.id) ?? [];
  const sides = [
    { side: "home" as const, own: moments.filter((m) => m.forTeam) },
    { side: "away" as const, own: moments.filter((m) => !m.forTeam) },
  ];
  return (
    <div className="match-card-board">
      <div className="match-card-head">
        <span className="match-card-key match-card-key-home" />
        <TeamName match={match} side="home" />
        <span className="match-card-score">
          {match.homeScore} – {match.awayScore}
        </span>
        <TeamName match={match} side="away" />
        <span className="match-card-key match-card-key-away" />
      </div>
      {moments.length > 0 && (
        <div className="match-card-moments">
          {sides.map((s) => (
            <div key={s.side} className={`match-card-moments-side match-card-moments-${s.side}`}>
              {s.own.map((m, k) => (
                <Moment key={k} m={m} match={match} side={s.side} womens={womens} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Possession, then a bar per stat (/match-stats). */
function MatchStats({ match }: { match: MatchRef }) {
  const teams = useLiveQuery(`match:stats:${match.id}`, () => loadMatchStats(liveApi(), match.id))!;
  const pair = homeAway(teams, match.home.id, match.away.id);
  if (!pair) return <div className="match-card-loading">No event data for this game yet.</div>;
  const [home, away] = pair;
  const rows = matchStatRows(home, away);
  return (
    <div className="match-card-stats">
      {home.possession !== null && away.possession !== null && <Possession home={home} away={away} />}
      {rows.map((r) => (
        <div key={r.key} className="match-stat" data-tooltip={r.note ?? undefined}>
          <div className="match-stat-line">
            <span className={r.leader === "home" ? "match-stat-lead" : undefined}>{r.home}</span>
            <span className="match-stat-label">{r.label}</span>
            <span className={r.leader === "away" ? "match-stat-lead" : undefined}>{r.away}</span>
          </div>
          {/* Each bar grows out from the middle: its side's share of the two. */}
          <div className="match-stat-bars">
            <div className="match-stat-half match-stat-half-home">
              <div className="match-stat-bar match-stat-bar-home" style={{ width: `${r.homeShare * 100}%` }} />
            </div>
            <div className="match-stat-half">
              <div className="match-stat-bar match-stat-bar-away" style={{ width: `${r.awayShare * 100}%` }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** A ring split by each team's share of the ball, its number beside it. */
function Possession({ home, away }: { home: MatchStatsTeam; away: MatchStatsTeam }) {
  const h = home.possession as number;
  const a = away.possession as number;
  // A circle of circumference 100, so a share is its own dash length. The home arc starts at the
  // top and runs anticlockwise, so it sits on the left, the home side.
  const R = 100 / (2 * Math.PI);
  return (
    <div className="match-possession">
      <div className="match-possession-label">Possession</div>
      <div className="match-possession-row">
        <span className={h > a ? "match-stat-lead" : undefined}>{h.toFixed(1)}%</span>
        <svg className="match-possession-ring" viewBox="0 0 42 42" aria-hidden="true">
          <circle cx="21" cy="21" r={R} className="match-possession-away" />
          <circle
            cx="21"
            cy="21"
            r={R}
            className="match-possession-home"
            strokeDasharray={`${h} ${100 - h}`}
            transform="translate(42 0) scale(-1 1) rotate(-90 21 21)"
          />
        </svg>
        <span className={a > h ? "match-stat-lead" : undefined}>{a.toFixed(1)}%</span>
      </div>
    </div>
  );
}

/** A goal or red card. The name drags out as the player, opening on this league and season. */
function Moment({ m, match, side, womens }: { m: TeamMatchMoment; match: MatchRef; side: "home" | "away"; womens: boolean | null }) {
  // Older servers don't send the id: the name then stays plain.
  const playerId = typeof m.playerId === "number" ? m.playerId : null;
  // An own goal is listed under the side it counts for; its scorer plays for the other one.
  const team = match[m.kind === "own-goal" ? (side === "home" ? "away" : "home") : side].name;
  const onPress = (e: ReactPointerEvent<HTMLSpanElement>) => {
    if (playerId === null || window.innerWidth <= 768 || e.button !== 0) return;
    e.stopPropagation();
    const id = playerId;
    startPlayerTokenDragOnMove(
      e.nativeEvent,
      () => {
        noteRowPlayer({ id, name: m.playerName ?? "", team }, womens);
        setStickyPlayer(id);
      },
      {
        _selectedSeason: match.season,
        _selectedSeasonPlayer: id,
        _eventMapPlayer: id,
        _eventMapSeason: match.season,
        _eventMapCompetitionId: String(match.competitionId),
      },
    );
  };
  return (
    <div className="match-card-moment">
      <span className={`match-card-mark match-card-mark-${m.kind}`} />
      <span className={`match-card-moment-name${playerId !== null ? " team-draggable" : ""}`} onPointerDown={onPress}>
        {momentInitialName(m)}
      </span>
      <span className="match-card-moment-minute">
        {m.minute !== null ? clockMinute(m.minute, m.period) : ""}
        {m.isPenalty ? " (pen)" : m.kind === "own-goal" ? " (OG)" : ""}
      </span>
    </div>
  );
}
